import React from 'react';
import CategoriesPage from './pages/CategoriesPage';
import CardapioV2Page from './pages/CardapioV2Page';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/context/AuthContext';
import { usePermissions, ROUTE_PERMISSIONS } from '@/hooks/usePermissions';
import { AccessDeniedPage } from '@/components/Layout';
import { Toaster } from '@/components/ui/sonner';
import { LoginPage } from '@/pages/LoginPage';
import { DashboardPage } from '@/pages/DashboardPage';
import { DeliveryPage } from '@/pages/DeliveryPage';
import { DeliveryKanbanPage } from '@/pages/DeliveryKanbanPage';
import { ProductsPage } from '@/pages/ProductsPage';
import { OrdersPage } from '@/pages/OrdersPage';
import { PDVPage } from '@/pages/PDVPage';
import { SalaoPage } from '@/pages/SalaoPage';
import { VendaRapidaPage } from '@/pages/VendaRapidaPage';
import { KDSPage } from '@/pages/KDSPage';
import { CustomersPage } from '@/pages/CustomersPage';
import { SettingsPage } from '@/pages/OtherPages';
import { HeatmapPage } from '@/pages/HeatmapPage';
import { MotoboyLedgerPage } from "@/pages/MotoboyLedgerPage";
import ReportsPage from '@/pages/ReportsPage';
import { CashPage } from '@/pages/CashPage';
import { FinancePage } from '@/pages/FinancePage';
import { FiscalPage } from '@/pages/FiscalPage';
import FiscalSefazPage from '@/pages/FiscalSefazPage';
import { StockPage } from '@/pages/StockPage';
import { CRMPage } from '@/pages/CRMPage';
import { DriversPage } from '@/pages/DriversPage';
import RBACPage from '@/pages/RBACPage';
import AnalyticsPage from '@/pages/AnalyticsPage';
import IFoodIntegrationPage from '@/pages/IFoodIntegrationPage';
import StockAlertsPublicPage from '@/pages/StockAlertsPublicPage';
import IFoodHomologationPage from '@/pages/IFoodHomologationPage';
import DriverSettlementPage from '@/pages/DriverSettlementPage';
import CODFinancePage from '@/pages/CODFinancePage';
import CieloGatewayPage from '@/pages/CieloGatewayPage';
import PrinterSettingsPage from '@/pages/PrinterSettingsPage';
import RoutingPage from '@/pages/RoutingPage';
import DigitalMenuPage from '@/pages/DigitalMenuPage';
import PublicMenuPage from '@/pages/PublicMenuPage';
import PrivacyPolicyPage from "@/pages/PrivacyPolicyPage";
import CheckoutPage from './pages/CheckoutPage';
import OrderSuccessPage from './pages/OrderSuccessPage';
import OrderTrackingPage from '@/pages/OrderTrackingPage';
import Didi99FoodPage from '@/pages/Didi99FoodPage';
import Didi99FoodV4Page from '@/pages/Didi99FoodV4Page';
import ChatbotMessagesPage from '@/pages/ChatbotMessagesPage';
import WhatsAppPage from '@/pages/WhatsAppPage';
import WhatsAppMonitorPage from "@/pages/WhatsAppMonitorPage";
import WhatsAppInboxPage from "@/pages/WhatsAppInboxPage";
import WhatsAppTemplatesPage from "@/pages/WhatsAppTemplatesPage";
import MonitoringPage from "@/pages/MonitoringPage";
import BotPage from '@/pages/BotPage';
import LandingPage from "@/pages/LandingPage";
import KeetaPage from "@/pages/KeetaPage";
import KeetaOperationsCenter from "@/components/keeta/KeetaOperationsCenter";
import DriverLogin from '@/pages/driver-app/DriverLogin';
import DriverDashboard from '@/pages/driver-app/DriverDashboard';
import '@/App.css';
import { POSWebPage } from '@/pages/pos-web/POSWebPage';

// Componente que verifica autenticação
function PrivateRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-slate-500">Carregando...</div>
      </div>
    );
  }

  return user ? children : <Navigate to="/login" />;
}

// Componente que verifica autenticação E permissões
function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const { canAccessRoute } = usePermissions();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-slate-500">Carregando...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  // Verificar permissão para a rota atual
  const currentPath = location.pathname;
  if (!canAccessRoute(currentPath)) {
    return <AccessDeniedPage />;
  }

  return children;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes - Cardápio Digital */}
      <Route path="/landing" element={<LandingPage />} />
      <Route path="/cardapio-v2" element={<CardapioV2Page />} />
      <Route path="/menu/:storeSlug" element={<PublicMenuPage />} />
      <Route path="/estoque-alertas" element={<StockAlertsPublicPage />} />
      <Route path="/privacidade" element={<PrivacyPolicyPage />} />
      <Route path="/checkout/:storeSlug" element={<CheckoutPage />} />
      <Route path="/menu/:storeSlug/order-success" element={<OrderSuccessPage />} />
      <Route path="/track/:token" element={<OrderTrackingPage />} />
      
      {/* Auth Routes */}
      <Route path="/login" element={<LoginPage />} />
      
      {/* Dashboard - acesso geral para autenticados */}
      <Route path="/" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      
      {/* KDS - requer KDS_VIEW */}
      <Route path="/kds" element={<ProtectedRoute><KDSPage /></ProtectedRoute>} />
      
      {/* PDV - requer PDV_VIEW */}
      <Route path="/pdv" element={<ProtectedRoute><PDVPage /></ProtectedRoute>} />
      <Route path="/salao" element={<ProtectedRoute><SalaoPage /></ProtectedRoute>} />
      <Route path="/pos-web" element={<ProtectedRoute><POSWebPage /></ProtectedRoute>} />
      <Route path="/venda-rapida" element={<ProtectedRoute><VendaRapidaPage /></ProtectedRoute>} />
      
      {/* Pedidos - requer PEDIDOS_VIEW */}
      <Route path="/orders" element={<ProtectedRoute><OrdersPage /></ProtectedRoute>} />
      
      {/* Delivery - requer DELIVERY_VIEW */}
      <Route path="/delivery" element={<Navigate to="/orders?view=kanban" replace />} />
      <Route path="/delivery/kanban" element={<Navigate to="/orders?view=kanban" replace />} />
      <Route path="/delivery/management" element={<ProtectedRoute><DeliveryPage /></ProtectedRoute>} />
      
      {/* CRM - requer CRM_VIEW */}
      <Route path="/heatmap" element={<ProtectedRoute><HeatmapPage /></ProtectedRoute>} />
      <Route path="/motoboy-ledger" element={<ProtectedRoute><MotoboyLedgerPage /></ProtectedRoute>} />
      
      {/* Produtos - requer PRODUTOS_VIEW */}
      <Route path="/products" element={<ProtectedRoute><ProductsPage /></ProtectedRoute>} />
      <Route path="/categories" element={<ProtectedRoute><CategoriesPage /></ProtectedRoute>} />
      
      {/* Clientes - requer CLIENTES_VIEW */}
      <Route path="/customers" element={<ProtectedRoute><CustomersPage /></ProtectedRoute>} />
      
      {/* Estoque - requer ESTOQUE_VIEW */}
      <Route path="/stock" element={<ProtectedRoute><StockPage /></ProtectedRoute>} />
      
      {/* Financeiro - requer FINANCEIRO_VIEW */}
      <Route path="/finance" element={<ProtectedRoute><FinancePage /></ProtectedRoute>} />
      <Route path="/cash" element={<ProtectedRoute><CashPage /></ProtectedRoute>} />
      <Route path="/cod-finance" element={<ProtectedRoute><CODFinancePage /></ProtectedRoute>} />
      <Route path="/integracoes/cielo-gateway" element={<ProtectedRoute><CieloGatewayPage /></ProtectedRoute>} />
      
      {/* Fiscal - requer FISCAL_VIEW */}
      <Route path="/fiscal" element={<ProtectedRoute><FiscalPage /></ProtectedRoute>} />
      <Route path="/sefaz" element={<ProtectedRoute><FiscalSefazPage /></ProtectedRoute>} />
      
      {/* Relatórios - requer RELATORIOS_VIEW */}
      <Route path="/reports" element={<ProtectedRoute><ReportsPage /></ProtectedRoute>} />
      
      {/* CRM - requer CRM_VIEW */}
      <Route path="/crm" element={<ProtectedRoute><CRMPage /></ProtectedRoute>} />
      <Route path="/analytics" element={<ProtectedRoute><AnalyticsPage /></ProtectedRoute>} />
      
      {/* Configurações - requer USUARIOS_VIEW ou CONFIGURACOES_VIEW */}
      <Route path="/rbac" element={<ProtectedRoute><RBACPage /></ProtectedRoute>} />
      <Route path="/ifood" element={<ProtectedRoute><IFoodIntegrationPage /></ProtectedRoute>} />
      <Route path="/ifood/homologation" element={<ProtectedRoute><IFoodHomologationPage /></ProtectedRoute>} />
      <Route path="/99food" element={<ProtectedRoute><Didi99FoodPage /></ProtectedRoute>} />
      <Route path="/99food-v4" element={<ProtectedRoute><Didi99FoodV4Page /></ProtectedRoute>} />
      <Route path="/keeta" element={<ProtectedRoute><KeetaPage /></ProtectedRoute>} />
      <Route path="/keeta-ops" element={<ProtectedRoute><KeetaOperationsCenter /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
      
      {/* Delivery COD - requer DELIVERY_VIEW */}
      <Route path="/drivers" element={<ProtectedRoute><DriversPage /></ProtectedRoute>} />
      <Route path="/driver-settlements" element={<ProtectedRoute><DriverSettlementPage /></ProtectedRoute>} />
      <Route path="/routing" element={<ProtectedRoute><RoutingPage /></ProtectedRoute>} />
      
      {/* Configurações de impressora e menu digital */}
      <Route path="/printer-settings" element={<ProtectedRoute><PrinterSettingsPage /></ProtectedRoute>} />
      <Route path="/digital-menu" element={<ProtectedRoute><DigitalMenuPage /></ProtectedRoute>} />
      
      {/* BOT Routes - Nova estrutura consolidada */}
      <Route path="/bot" element={<Navigate to="/bot/config" replace />} />
      <Route path="/bot/config" element={<ProtectedRoute><BotPage /></ProtectedRoute>} />
      <Route path="/bot/conversas" element={<ProtectedRoute><BotPage /></ProtectedRoute>} />
      <Route path="/bot/whatsapp" element={<ProtectedRoute><BotPage /></ProtectedRoute>} />
        <Route path="/whatsapp-monitor" element={<ProtectedRoute><WhatsAppMonitorPage /></ProtectedRoute>} />
        <Route path="/whatsapp-inbox" element={<ProtectedRoute><WhatsAppInboxPage /></ProtectedRoute>} />
        <Route path="/whatsapp-templates" element={<ProtectedRoute><WhatsAppTemplatesPage /></ProtectedRoute>} />
        <Route path="/monitoramento" element={<ProtectedRoute><MonitoringPage /></ProtectedRoute>} />
      <Route path="/bot/mensagens" element={<ProtectedRoute><BotPage /></ProtectedRoute>} />
      <Route path="/bot/fluxos" element={<ProtectedRoute><BotPage /></ProtectedRoute>} />
      <Route path="/bot/webhooks" element={<ProtectedRoute><BotPage /></ProtectedRoute>} />
      <Route path="/bot/logs" element={<ProtectedRoute><BotPage /></ProtectedRoute>} />
      
      {/* Rotas legadas - Redirecionam para novo menu BOT */}
      <Route path="/chatbot-messages" element={<Navigate to="/bot/mensagens" replace />} />
      <Route path="/whatsapp" element={<Navigate to="/bot/whatsapp" replace />} />
      
      {/* Driver App - Public Routes (não requer auth do admin) */}
      <Route path="/driver-app" element={<DriverDashboard />} />
      <Route path="/driver-app/login" element={<DriverLogin />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
